import { toast } from "react-toastify";

import {
  SaveAgenticLogicRuleDraftRequestBodyType,
  SaveAgenticLogicRuleDraftRequestPathParamsType,
  SaveAgenticLogicRuleDraftResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/draft/save-draft";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

/**
 * Saves the working draft. The verb is PUT (the shipping-provider equivalent
 * is POST), and `source` is sent inline as one file's contents — the backend
 * bundles it directly, so a serialized multi-file manifest would be compiled
 * as code.
 */
const saveAgenticLogicRuleDraft = async (
  payload: ApiRequest<
    SaveAgenticLogicRuleDraftRequestBodyType,
    SaveAgenticLogicRuleDraftRequestPathParamsType
  >
) => {
  const resp = await api.put<
    SaveAgenticLogicRuleDraftResponseBodyType,
    SaveAgenticLogicRuleDraftRequestBodyType
  >(`/v2/agentic-logic/rules/${payload.pathParams.ruleId}/draft`, payload.body);
  return resp.data;
};

export const useSaveAgenticLogicRuleDraft = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SaveAgenticLogicRuleDraftResponseBodyType,
    Error,
    ApiRequest<
      SaveAgenticLogicRuleDraftRequestBodyType,
      SaveAgenticLogicRuleDraftRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => saveAgenticLogicRuleDraft(data),
    onSuccess: (data, payload) => {
      // The response carries the freshly persisted draft, so seed the read
      // cache instead of forcing a round trip for what we already hold.
      queryClient.setQueryData<SaveAgenticLogicRuleDraftResponseBodyType>(
        agenticLogicKeys.draft(payload.pathParams.ruleId),
        { draft: data.draft }
      );
      // A save moves the source revision: the scenario set may be stale (the
      // backend re-checks coverage) and every cached evaluation answered for
      // code that no longer exists.
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.scenarios(payload.pathParams.ruleId),
      });
      // A save that carries a generated suite moves the inventory: its test
      // positions are only valid for the suite text they were read from. A
      // save without one leaves the suite alone, and a refetch it started
      // would race an adoption that follows it (save-then-adopt is one
      // gesture in the editor) and land a stale inventory over the adopt's.
      if (payload.body.generatedTestSuite !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.generatedTests(payload.pathParams.ruleId),
        });
      }
      // REMOVED, not invalidated: nothing on this client can re-run a cart
      // (Atlas owns the evaluate route), so an invalidated entry would sit
      // there stale forever. Dropping it is what retires the run the canvas
      // is showing.
      queryClient.removeQueries({
        predicate: (query) => {
          const [key] = query.queryKey as [Record<string, unknown> | undefined];
          return (
            key?.scenarioEvaluation === true &&
            key.ruleId === payload.pathParams.ruleId
          );
        },
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to save draft${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
